import React, { useEffect, useState } from 'react';
import { Box, Flex, Text, useDisclosure, useTheme, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ICreateStationPayload } from '../../interfaces/Station/ICreateStationPayload.ts';
import { overviewContainerStyles } from '../../styles/ts/overviewStyles.ts';
import { useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import CareHomePageSkeleton from './CareHomePageSkeleton.tsx';
import AleaEditCareHomeModal from '../../modals/AleaEditCareHomeModal/AleaEditCareHomeModal.tsx';
import AleaNewLivingAreaModal from '../../modals/AleaNewLivingAreaModal/AleaNewLivingAreaModal.tsx';
import { IStation } from '../../interfaces/Station/IStation.ts';
import AleaConfirmModal from '../../components/AleaModal/AleaConfirmModal.tsx';
import AleaLoader from '../../components/AleaLoader/AleaLoader.tsx';
import AleaInfoModal from '../../modals/AleaInfoModal/AleaInfoModal.tsx';
import InviteNewUserModal from '../../modals/InviteNewUserModal/InviteNewUserModal.tsx';
import AccountSettingsModal from '../../modals/AccountSettingsModal/AccountSettingsModal.tsx';
import AleaModal from '../../components/AleaModal/AleaModal.tsx';
import { UserRole } from '../../constants/UserRoles.ts';
import {
  getCareHomeQueryKey,
  getCockpitUsersQueryKey,
  getStationsQueryKey,
  useCareHomeQuery,
  useCockpitUsersQuery,
  useStationsQuery,
} from './careHomePage.queries.ts';
import { useCareHomePageMutations } from './careHomePage.mutations.ts';
import LivingAreasSection from './components/LivingAreasSection.tsx';
import CockpitUsersSection from './components/CockpitUsersSection.tsx';
import CareHomeDetailsSection from './components/CareHomeDetailsSection.tsx';
import CareHomeHeader from './components/CareHomeHeader.tsx';

const CareHomePage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const toast = useToast();
  const theme = useTheme();

  // State for row action menus & dialogs
  const [activeActionRowId, setActiveActionRowId] = useState<string | null>(null);
  const [editingStation, setEditingStation] = useState<IStation | null>(null);
  const [stationToDelete, setStationToDelete] = useState<IStation | null>(null);

  // Single info modal for simple success messages (auto-closes)
  const [infoModalMessageKey, setInfoModalMessageKey] = useState<string | null>(null);
  const [shouldShowInviteInfoAfterClose, setShouldShowInviteInfoAfterClose] = useState<boolean>(false);
  const [isDeleteErrorOpen, setIsDeleteErrorOpen] = useState<boolean>(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const { user } = useAuth();
  const targetCareHomeId = id || user?.carehome?.id;

  // React Query keys (centralized in a separate file)
  const careHomeQueryKey = getCareHomeQueryKey(targetCareHomeId);
  const stationsQueryKey = getStationsQueryKey(targetCareHomeId);
  const cockpitUsersQueryKey = getCockpitUsersQueryKey(targetCareHomeId);

  // Data (cached via React Query)
  const { data: stations = [], isLoading: isStationsLoading } = useStationsQuery(targetCareHomeId);
  const { data: cockpitUsers = [], isLoading: isCockpitUsersLoading } = useCockpitUsersQuery(targetCareHomeId);
  const { data: careHomeData, isLoading: isCareHomeLoading } = useCareHomeQuery(targetCareHomeId);

  // Modal controls
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isLivingAreaOpen, onOpen: onOpenLivingArea, onClose: onCloseLivingArea } = useDisclosure();
  const { isOpen: isInviteOpen, onOpen: onOpenInvite, onClose: onCloseInvite } = useDisclosure();
  const {
    isOpen: isAccountSettingsOpen,
    onOpen: onOpenAccountSettings,
    onClose: onCloseAccountSettings,
  } = useDisclosure();

  // CRUD actions are handled as React Query mutations (extracted to a separate file)
  const {
    updateCareHomeStatusMutation,
    upsertStationMutation,
    deleteStationMutation,
    resendInviteMutation,
    isCrudLoading,
    setCareHomeCache,
    invalidateCockpitUsers,
  } = useCareHomePageMutations(
    { careHomeQueryKey, stationsQueryKey, cockpitUsersQueryKey },
    {
      onLivingAreaDone: onCloseLivingArea,
      onClearEditingStation: () => setEditingStation(null),
      onClearStationToDelete: () => setStationToDelete(null),
      onShowEditInfo: () => setInfoModalMessageKey('changesAreBeingSaved'),
      onShowDeleteInfo: () => setInfoModalMessageKey('careHome.livingAreaDeletedSuccess'),
      onShowDeleteError: () => setIsDeleteErrorOpen(true),
      onShowResendInfo: () => setInfoModalMessageKey('invitationEmailHasBeenSent'),
    }
  );

  // Add a function to check if user can edit
  const canEdit = () => {
    return user?.role !== 'Organisation manager';
  };

  // Helper to close info modal manually
  const closeInfoModal = () => setInfoModalMessageKey(null);

  // Auto-close info modal after 2 seconds
  useEffect(() => {
    if (!infoModalMessageKey) return;
    const timer = setTimeout(() => closeInfoModal(), 2000);
    return () => clearTimeout(timer);
  }, [infoModalMessageKey]);

  // Show invite info only after the Add User modal has actually closed
  useEffect(() => {
    if (!isInviteOpen && shouldShowInviteInfoAfterClose) {
      setInfoModalMessageKey('invitationEmailHasBeenSent');
      setShouldShowInviteInfoAfterClose(false);
    }
  }, [isInviteOpen, shouldShowInviteInfoAfterClose]);

  /**
   * Handles the edit action by opening the edit modal
   */
  const handleEdit = () => {
    onOpen();
  };

  // Handle Archive button click
  const handleArchiveClick = () => {
    setIsArchiveModalOpen(true);
  };

  // Handle Archive Confirmation
  const handleArchiveConfirm = async () => {
    if (!careHomeData) return;

    try {
      await updateCareHomeStatusMutation.mutateAsync({
        careHomeId: careHomeData.id,
        isActive: false,
      });
      toast({
        title: t('success'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsArchiveModalOpen(false);
    } catch (error) {
      console.error('Error archiving care home:', error);
      toast({
        title: t('error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle Restore button click
  const handleRestoreClick = () => {
    setIsRestoreModalOpen(true);
  };

  // Handle Restore Confirmation
  const handleRestoreConfirm = async () => {
    if (!careHomeData) return;

    try {
      await updateCareHomeStatusMutation.mutateAsync({
        careHomeId: careHomeData.id,
        isActive: true,
      });
      toast({
        title: t('success'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsRestoreModalOpen(false);
    } catch (error) {
      console.error('Error restoring care home:', error);
      toast({
        title: t('error'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  /**
   * Handles confirm of creating a new living area (station)
   */
  const handleConfirmNewLivingArea = async (values: {
    livingAreaName: string;
    telephone: { code: string; phone: string };
  }) => {
    if (!targetCareHomeId) {
      console.error('Care home ID is missing');
      onCloseLivingArea();
      return;
    }

    const payload: ICreateStationPayload = {
      carehome_id: targetCareHomeId,
      is_active: true,
      name: values.livingAreaName,
      compound_phone_number: values.telephone,
      extra_data: null,
    };

    try {
      const stationId = editingStation?.id;
      const isEdit = !!editingStation;
      await upsertStationMutation.mutateAsync({ stationId, payload, isEdit });
    } catch (e) {
      console.error('Failed to create living area', e);
    }
  };

  if (isCareHomeLoading || !careHomeData) {
    return <CareHomePageSkeleton />;
  }

  const isArchived = !careHomeData.is_active;
  const isCareHomeManager = user?.role === UserRole.CAREHOME_MANAGER;
  const isOrganisationManager = user?.role === UserRole.ORGANISATION_MANAGER;

  return (
    <Flex flexFlow="column" borderRadius="20px">
      <CareHomeHeader
        title={careHomeData.name}
        isCareHomeManager={isCareHomeManager}
        isOrganisationManager={!!isOrganisationManager}
        isArchived={isArchived}
        onArchiveClick={handleArchiveClick}
        onRestoreClick={handleRestoreClick}
      />
      <Flex sx={overviewContainerStyles}>
        <CareHomeDetailsSection
          isArchived={isArchived}
          canEdit={canEdit()}
          careHomeData={careHomeData}
          onEdit={handleEdit}
        />

        <LivingAreasSection
          isArchived={isArchived}
          canEdit={canEdit()}
          stations={stations}
          isStationsLoading={isStationsLoading}
          activeActionRowId={activeActionRowId}
          onSetActiveActionRowId={setActiveActionRowId}
          onCreateNewLivingArea={() => {
            setEditingStation(null);
            onOpenLivingArea();
          }}
          onEditLivingArea={(station) => {
            setEditingStation(station);
            onOpenLivingArea();
          }}
          onDeleteLivingArea={(station) => {
            setStationToDelete(station);
          }}
        />

        <CockpitUsersSection
          isArchived={isArchived}
          canEdit={canEdit()}
          cockpitUsers={cockpitUsers}
          isCockpitUsersLoading={isCockpitUsersLoading}
          activeActionRowId={activeActionRowId}
          onSetActiveActionRowId={setActiveActionRowId}
          currentUserId={user?.id}
          onOpenInvite={onOpenInvite}
          onOpenAccountSettings={onOpenAccountSettings}
          onResendInvite={(accountId) => resendInviteMutation.mutate(accountId)}
        />
      </Flex>

      <AleaEditCareHomeModal
        isOpen={isOpen}
        onClose={onClose}
        careHome={careHomeData}
        onSaved={(updated) => {
          // Keep care home detail cache in sync after edit
          setCareHomeCache(updated);
        }}
      />

      <AleaNewLivingAreaModal
        isOpen={isLivingAreaOpen}
        onClose={onCloseLivingArea}
        onConfirm={handleConfirmNewLivingArea}
        initialValues={
          editingStation
            ? {
                livingAreaName: editingStation.name,
                telephone: editingStation.compound_phone_number || {
                  code: '+49',
                  phone: '',
                },
              }
            : undefined
        }
      />

      <InviteNewUserModal
        isOpen={isInviteOpen}
        onClose={onCloseInvite}
        onConfirm={async () => {
          // Show success info as soon as the modal closes,
          // don't wait for the cockpit users refresh to finish
          setShouldShowInviteInfoAfterClose(true);
          // Refresh cockpit users list after inviting a new user
          await invalidateCockpitUsers();
        }}
      />

      <AccountSettingsModal isOpen={isAccountSettingsOpen} onClose={onCloseAccountSettings} />

      {/* Backdrop when row action menu is open */}
      {activeActionRowId && (
        <Box
          position="fixed"
          top={0}
          left={0}
          w="100vw"
          h="100vh"
          bg="rgba(0, 0, 0, 0.20)"
          zIndex={1500}
          onClick={() => setActiveActionRowId(null)}
        />
      )}

      {/* Confirm delete modal */}
      <AleaConfirmModal
        isOpen={!!stationToDelete}
        onClose={() => setStationToDelete(null)}
        title={t('confirmDeleteLivingArea')}
        onCancelButtonClick={() => setStationToDelete(null)}
        onConfirm={async () => {
          if (!stationToDelete) return;
          // mutateAsync rejects on error; we handle errors inside the mutation callbacks
          await deleteStationMutation.mutateAsync(stationToDelete.id).catch(() => undefined);
        }}
      />

      {isCrudLoading && <AleaLoader />}

      <AleaInfoModal
        isOpen={!!infoModalMessageKey}
        onClose={closeInfoModal}
        message={infoModalMessageKey ? t(infoModalMessageKey) : ''}
      />

      <AleaConfirmModal
        isOpen={isDeleteErrorOpen}
        onClose={() => setIsDeleteErrorOpen(false)}
        title={t('livingAreaCanNotBeDeletedHeadline')}
        description={t('livingAreaCanNotBeDeletedSubline')}
        hideCancelButton
        titleColor="sunsetEmber.500"
        onConfirm={() => setIsDeleteErrorOpen(false)}
      />

      {/* Archive Confirmation Modal */}
      <AleaModal
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        title={t('archiveCareHomeConfirmationHeadline', {
          name: careHomeData?.name,
        })}
        onConfirm={handleArchiveConfirm}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        variant="confirm"
        titleColor="sunsetEmber.500"
      >
        <Box px="34px">
          <Text fontSize="18px" fontStyle="normal" fontWeight={500} lineHeight="normal" mb="12px">
            {t('archiveCareHomeConfirmationDescription1')}
          </Text>
          <Text fontSize="18px" fontStyle="normal" fontWeight={400} lineHeight="normal" mb="12px">
            {t('archiveCareHomeConfirmationDescription2')}
          </Text>
          <Text fontSize="18px" fontStyle="normal" fontWeight={400} lineHeight="normal" mb="12px">
            {t('resident.undoableInfo')}
          </Text>
        </Box>
      </AleaModal>

      {/* Restore Confirmation Modal */}
      <AleaModal
        isOpen={isRestoreModalOpen}
        onClose={() => setIsRestoreModalOpen(false)}
        title={t('general.doYouApprove', {
          name: careHomeData?.name,
        })}
        onConfirm={handleRestoreConfirm}
        confirmLabel={t('confirm')}
        cancelLabel={t('cancel')}
        variant="confirm"
        titleColor={theme.colors.alea[500]}
      ></AleaModal>
    </Flex>
  );
};

export default CareHomePage;
